import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getPublicStore, publicStoreTake, publicStoreReverseTake } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import { CheckCircle2, Warehouse, MapPin, Search, Hash, Minus, Package, Building2, ScanLine, Zap, Undo2, History } from 'lucide-react';
import LutromLogo from '../components/LutromLogo';
import PoweredByLutrom from '../components/PoweredByLutrom';
import OutOfHoursBanner from '../components/OutOfHoursBanner';
import { QrScanner } from '../components/QrScanner';
import { toast } from 'sonner';

const OP_KEY = 'lutrom_operator_v1';

const loadOperator = () => {
  try {
    const raw = localStorage.getItem(OP_KEY);
    if (!raw) return { name: '', email: '', phone: '' };
    const parsed = JSON.parse(raw);
    return { name: parsed.name || '', email: parsed.email || '', phone: parsed.phone || '' };
  } catch { return { name: '', email: '', phone: '' }; }
};

const PublicStorePickup = () => {
  const { storeId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  // Operator identity (sticky between scans on the same device)
  const [operator, setOperator] = useState(loadOperator());
  const [identityConfirmed, setIdentityConfirmed] = useState(false);

  // Take dialog
  const [takeRow, setTakeRow] = useState(null); // { part, stockRow }
  const [qty, setQty] = useState(1);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [lastConfirmation, setLastConfirmation] = useState(null);
  const [scanOpen, setScanOpen] = useState(false);

  // Session pickup history (most recent first). Each entry:
  //   { movementId, partName, qty, locationName, takenAt, reversed }
  // Only the latest *non-reversed* entry shows an Undo button. We keep up
  // to 8 in state but only render 5 to keep the strip compact.
  const [history, setHistory] = useState([]);
  const [undoing, setUndoing] = useState(null); // movementId being reversed

  const pushHistory = (entry) => {
    setHistory((h) => [entry, ...h].slice(0, 8));
  };

  // Quick mode: scan → instantly take 1 → re-open camera. Sticky per-device
  // so a stockroom run feels like a barcode gun: scan, beep, scan, beep.
  const [quickMode, setQuickMode] = useState(() => {
    try { return localStorage.getItem('lutrom_quickmode_v1') === '1'; }
    catch { return false; }
  });
  const toggleQuickMode = () => {
    setQuickMode((on) => {
      const next = !on;
      try { localStorage.setItem('lutrom_quickmode_v1', next ? '1' : '0'); } catch { /* ignore */ }
      return next;
    });
  };

  const refresh = () => getPublicStore(storeId).then((d) => { setData(d); setError(false); }).catch(() => setError(true));
  useEffect(() => { refresh().finally(() => setLoading(false)); /* eslint-disable-next-line */ }, [storeId]);

  // If we already have a name from a prior visit, skip the prompt.
  useEffect(() => {
    if (operator.name.trim()) setIdentityConfirmed(true);
  }, []); // eslint-disable-line

  const filtered = useMemo(() => {
    if (!data) return [];
    const list = data.parts || [];
    if (!search.trim()) return list;
    const q = search.toLowerCase();
    return list.filter(b =>
      (b.part.name || '').toLowerCase().includes(q) ||
      (b.part.sku || '').toLowerCase().includes(q) ||
      (b.part.description || '').toLowerCase().includes(q) ||
      (b.part.barcode || '').toLowerCase().includes(q)
    );
  }, [data, search]);

  const persistOperator = (next) => {
    setOperator(next);
    try { localStorage.setItem(OP_KEY, JSON.stringify(next)); } catch { /* localStorage may be blocked */ }
  };

  const confirmIdentity = () => {
    if (!operator.name.trim()) return toast.error('Please enter your name');
    persistOperator(operator);
    setIdentityConfirmed(true);
    // Fresh session — clear any history left over from a prior operator.
    setHistory([]);
    setLastConfirmation(null);
  };

  const openTake = (part, stockRow) => {
    setTakeRow({ part, stockRow });
    setQty(1);
    setNotes('');
    setLastConfirmation(null);
  };

  // Take exactly 1 of (part, stockRow) without opening the dialog.
  // Used by Quick Mode after a barcode scan.
  const quickTakeOne = async (part, stockRow) => {
    try {
      const res = await publicStoreTake(storeId, {
        partId: part.id,
        locationId: stockRow.locationId,
        qty: 1,
        operatorName: operator.name.trim(),
        operatorEmail: operator.email.trim(),
        operatorPhone: operator.phone.trim(),
        notes: '',
      });
      setLastConfirmation({ ...res, partName: res.partName || part.name });
      pushHistory({
        movementId: res.movementId,
        partName: res.partName || part.name,
        qty: res.qty || 1,
        locationName: stockRow.locationName,
        takenAt: Date.now(),
        reversed: false,
      });
      await refresh();
      // Re-open scanner for the next item — slight delay so the toast
      // shows and the previous camera teardown completes cleanly.
      setTimeout(() => setScanOpen(true), 350);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Pickup failed');
    }
  };

  const handleScan = (decoded) => {
    setScanOpen(false);
    const raw = (decoded || '').trim();
    if (!raw) return;

    // Normalise: scanners often emit a full URL (e.g. /qr/store/ST-123 or
    // even another product page). Pull out the last meaningful token so an
    // operator pointing the camera at any sticker just works.
    const token = (() => {
      try {
        const u = new URL(raw);
        const last = u.pathname.split('/').filter(Boolean).pop();
        return last || raw;
      } catch { /* not a URL — use as-is */ return raw; }
    })().trim();
    const lower = token.toLowerCase();

    // Match this token against parts in the current store. Priority:
    // exact barcode > exact sku > exact id > substring on name.
    const inStore = data?.parts || [];
    const exact = inStore.find(b => {
      const p = b.part;
      return [p.barcode, p.sku, p.id].some(x => x && String(x).toLowerCase() === lower);
    });
    const target = exact || inStore.find(b =>
      (b.part.name || '').toLowerCase().includes(lower)
    );

    if (!target) {
      toast.error(`No part matching "${token}" in this store`);
      return;
    }

    // Pick the highest-stock location automatically. Operator can still
    // change qty before confirming (unless Quick Mode is on).
    const bestRow = [...target.stock].sort((a, b) => b.quantity - a.quantity)[0];
    if (!bestRow || bestRow.quantity <= 0) {
      toast.error(`${target.part.name} is out of stock here`);
      return;
    }

    if (quickMode) {
      // One-tap: take 1, then re-open camera for the next scan.
      toast.success(`Took 1 × ${target.part.name}`);
      quickTakeOne(target.part, bestRow);
    } else {
      toast.success(`Found ${target.part.name}`);
      openTake(target.part, bestRow);
    }
  };

  const submitTake = async () => {
    if (!takeRow) return;
    const max = takeRow.stockRow.quantity;
    const n = parseInt(qty, 10);
    if (!Number.isFinite(n) || n <= 0) return toast.error('Enter a valid quantity');
    if (n > max) return toast.error(`Only ${max} available`);
    setSubmitting(true);
    try {
      const res = await publicStoreTake(storeId, {
        partId: takeRow.part.id,
        locationId: takeRow.stockRow.locationId,
        qty: n,
        operatorName: operator.name.trim(),
        operatorEmail: operator.email.trim(),
        operatorPhone: operator.phone.trim(),
        notes: notes.trim(),
      });
      persistOperator(operator);
      setLastConfirmation({ ...res, partName: res.partName || takeRow.part.name });
      pushHistory({
        movementId: res.movementId,
        partName: res.partName || takeRow.part.name,
        qty: res.qty || n,
        locationName: takeRow.stockRow.locationName,
        takenAt: Date.now(),
        reversed: false,
      });
      setTakeRow(null);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Pickup failed');
    } finally {
      setSubmitting(false);
    }
  };

  const undoLast = async (entry) => {
    if (!entry || entry.reversed || undoing) return;
    setUndoing(entry.movementId);
    try {
      await publicStoreReverseTake(storeId, entry.movementId, {
        operatorName: operator.name.trim(),
      });
      setHistory((h) => h.map(e =>
        e.movementId === entry.movementId ? { ...e, reversed: true } : e
      ));
      toast.success(`Undone: ${entry.qty} × ${entry.partName} restored`);
      setLastConfirmation(null);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not undo');
    } finally {
      setUndoing(null);
    }
  };

  if (error) return (
    <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-6">
      <OutOfHoursBanner />
      <div className="max-w-md w-full bg-white border border-slate-200 rounded-md p-8 text-center mt-6">
        <h1 className="font-display uppercase text-xl text-slate-900 mb-2">Invalid QR Code</h1>
        <p className="text-sm text-slate-600 mb-4">This QR code could not be matched to an active store.</p>
        <Link to="/"><Button>Go Home</Button></Link>
      </div>
      <PoweredByLutrom />
    </div>
  );

  if (loading || !data) return (
    <div className="min-h-screen flex flex-col">
      <OutOfHoursBanner />
      <div className="flex-1 flex items-center justify-center text-slate-500">Loading store...</div>
    </div>
  );

  const { store, parts } = data;

  return (
    <div className="min-h-screen bg-slate-50">
      <OutOfHoursBanner />
      <header className="bg-slate-900 text-white">
        <div className="max-w-3xl mx-auto p-6 flex items-center justify-between">
          <LutromLogo dark />
          <div className="text-right">
            <div className="text-[10px] tracking-[0.3em] text-slate-300 uppercase">Operator pickup</div>
            <div className="font-display uppercase tracking-wide text-sm">No login required</div>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto p-4 sm:p-6 space-y-5">
        {/* Store header card */}
        <div className="bg-white border border-slate-200 rounded-md p-5">
          <div className="text-xs font-medium uppercase tracking-widest text-blue-700 mb-2">Spares Store</div>
          <div className="flex items-start gap-3">
            <div className="bg-slate-900 text-white rounded p-2.5"><Warehouse className="h-5 w-5" /></div>
            <div className="min-w-0 flex-1">
              <h1 className="font-display uppercase text-2xl text-slate-900 leading-tight">{store.name}</h1>
              {store.customerName && (
                <div className="flex items-center gap-1.5 text-sm text-slate-600 mt-1">
                  <Building2 className="h-3.5 w-3.5" /> {store.customerName}
                </div>
              )}
              <div className="text-[11px] uppercase tracking-wider text-slate-400 mt-1.5">
                {store.kind === 'customer' ? 'Customer-owned' : 'Lutrom-owned'}
              </div>
            </div>
          </div>
        </div>

        {/* Last pickup confirmation banner */}
        {lastConfirmation && (
          <div
            data-testid="pickup-confirm-banner"
            className="bg-green-50 border border-green-200 rounded-md p-4 flex items-center gap-3"
          >
            <CheckCircle2 className="h-5 w-5 text-green-600 shrink-0" />
            <div className="text-sm text-green-900">
              Took <strong>{lastConfirmation.qty} × {lastConfirmation.partName}</strong>.
              {' '}{lastConfirmation.newQuantity} now on hand.
            </div>
          </div>
        )}

        {/* Identity gate (one-time per device) */}
        {!identityConfirmed && (
          <div className="bg-white border border-slate-200 rounded-md p-5">
            <div className="text-xs font-medium uppercase tracking-widest text-blue-700 mb-3">Step 1 of 2</div>
            <h2 className="font-display uppercase text-base text-slate-900 mb-1">Who's picking up?</h2>
            <p className="text-sm text-slate-600 mb-4">
              We log every pickup against your name. Only takes a moment — your details stay on this device for next time.
            </p>
            <div className="space-y-3">
              <div>
                <Label>Your name *</Label>
                <Input
                  className="mt-1.5"
                  value={operator.name}
                  onChange={e => setOperator({ ...operator, name: e.target.value })}
                  placeholder="e.g. Sam Carter"
                  data-testid="operator-name-input"
                />
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label>Email (optional)</Label>
                  <Input
                    className="mt-1.5"
                    type="email"
                    value={operator.email}
                    onChange={e => setOperator({ ...operator, email: e.target.value })}
                    placeholder="sam@..."
                    data-testid="operator-email-input"
                  />
                </div>
                <div>
                  <Label>Phone (optional)</Label>
                  <Input
                    className="mt-1.5"
                    value={operator.phone}
                    onChange={e => setOperator({ ...operator, phone: e.target.value })}
                    placeholder="+44..."
                    data-testid="operator-phone-input"
                  />
                </div>
              </div>
              <Button
                onClick={confirmIdentity}
                className="w-full h-11 bg-slate-900 hover:bg-slate-800 mt-2"
                data-testid="confirm-identity-btn"
              >
                Continue to stock
              </Button>
            </div>
          </div>
        )}

        {/* Stock list */}
        {identityConfirmed && (
          <>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="text-sm text-slate-600">
                Picking up as <span className="font-semibold text-slate-900">{operator.name}</span>
                <button
                  onClick={() => setIdentityConfirmed(false)}
                  className="ml-2 text-xs text-blue-700 hover:text-blue-900 underline"
                  data-testid="change-operator-btn"
                >
                  not you?
                </button>
              </div>
              <label
                className={`inline-flex items-center gap-2 cursor-pointer select-none px-3 py-1.5 rounded-md border text-sm font-medium transition-colors ${
                  quickMode
                    ? 'bg-blue-700 border-blue-700 text-white'
                    : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50'
                }`}
                data-testid="quick-mode-toggle"
              >
                <input
                  type="checkbox"
                  className="sr-only"
                  checked={quickMode}
                  onChange={toggleQuickMode}
                />
                <Zap className={`h-3.5 w-3.5 ${quickMode ? 'fill-current' : ''}`} />
                Quick mode {quickMode ? 'ON' : 'OFF'}
              </label>
            </div>
            {quickMode && (
              <div
                data-testid="quick-mode-hint"
                className="text-xs text-blue-900 bg-blue-50 border border-blue-200 rounded-md px-3 py-2"
              >
                Each scan takes <strong>1</strong> and immediately reopens the camera. Toggle off to set quantities or add notes.
              </div>
            )}

            {/* Session pickup history (last 5). Most recent at the top.
                Only the latest non-reversed entry shows an Undo button so
                we never let an operator skip back through arbitrary history
                — keeps the audit trail clean. */}
            {history.length > 0 && (
              <div
                data-testid="pickup-history"
                className="bg-white border border-slate-200 rounded-md overflow-hidden"
              >
                <div className="px-4 py-2 bg-slate-50 border-b border-slate-200 flex items-center gap-2 text-xs uppercase tracking-wider text-slate-600 font-semibold">
                  <History className="h-3.5 w-3.5" />
                  This session ({history.filter(e => !e.reversed).length})
                </div>
                <ul className="divide-y divide-slate-100">
                  {history.slice(0, 5).map((entry, idx) => {
                    // Find the latest non-reversed entry — that's the only
                    // one we let an operator undo from the strip.
                    const firstActiveIdx = history.findIndex(e => !e.reversed);
                    const isLatestActive = idx === firstActiveIdx && !entry.reversed;
                    return (
                      <li
                        key={entry.movementId}
                        data-testid={`history-row-${entry.movementId}`}
                        className={`flex items-center justify-between gap-3 px-4 py-2.5 text-sm ${
                          entry.reversed ? 'opacity-50 line-through' : ''
                        }`}
                      >
                        <div className="min-w-0 flex-1">
                          <span className="font-mono text-slate-500 mr-2">{entry.qty} ×</span>
                          <span className="text-slate-900 font-medium">{entry.partName}</span>
                          {entry.locationName && (
                            <span className="text-slate-500"> · {entry.locationName}</span>
                          )}
                        </div>
                        {isLatestActive ? (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => undoLast(entry)}
                            disabled={undoing === entry.movementId}
                            data-testid={`undo-${entry.movementId}`}
                            className="h-7 text-xs"
                          >
                            <Undo2 className="h-3 w-3 mr-1" />
                            {undoing === entry.movementId ? 'Undoing...' : 'Undo'}
                          </Button>
                        ) : entry.reversed ? (
                          <span className="text-[10px] uppercase tracking-wider text-slate-400">Reversed</span>
                        ) : null}
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  data-testid="part-search-input"
                  placeholder="Search by part name, SKU, barcode..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  className="pl-9 bg-white"
                />
              </div>
              <Button
                type="button"
                variant="outline"
                onClick={() => setScanOpen(true)}
                className="shrink-0 bg-white"
                data-testid="scan-barcode-btn"
              >
                <ScanLine className="h-4 w-4 mr-2" /> Scan
              </Button>
            </div>

            {parts.length === 0 && (
              <div className="p-12 text-center text-slate-500 bg-white border border-dashed border-slate-200 rounded-md">
                <Package className="h-10 w-10 mx-auto mb-3 text-slate-400" />
                This store is currently empty.
              </div>
            )}
            {parts.length > 0 && filtered.length === 0 && (
              <div className="p-8 text-center text-slate-500 bg-white border border-dashed border-slate-200 rounded-md">
                No parts match your search.
              </div>
            )}

            <div className="space-y-3">
              {filtered.map(({ part, totalStock, stock }) => (
                <div
                  key={part.id}
                  data-testid={`store-part-${part.id}`}
                  className="bg-white border border-slate-200 rounded-md p-4"
                >
                  <div className="flex items-start justify-between gap-3 mb-3">
                    {part.photoUrl && (
                      <img
                        src={`${process.env.REACT_APP_BACKEND_URL?.replace(/\/+$/, '')}${part.photoUrl}`}
                        alt={part.name}
                        className="h-14 w-14 rounded object-cover border border-slate-200 shrink-0"
                        data-testid={`store-part-photo-${part.id}`}
                      />
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 text-xs text-slate-500 font-mono">
                        {part.sku && <span>{part.sku}</span>}
                        {part.barcode && <span className="flex items-center gap-1"><Hash className="h-3 w-3" />{part.barcode}</span>}
                      </div>
                      <h3 className="font-display uppercase text-base text-slate-900 tracking-wide mt-0.5">
                        {part.name}
                      </h3>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="text-[10px] uppercase tracking-wider text-slate-500">In stock</div>
                      <div className="text-xl font-display text-slate-900">{totalStock}</div>
                    </div>
                  </div>
                  <div className="space-y-1 pt-2 border-t border-slate-100">
                    {stock.map(row => (
                      <div
                        key={row.id}
                        className="flex items-center justify-between gap-3 text-sm py-1.5"
                      >
                        <div className="min-w-0 flex items-center gap-2 text-slate-700 flex-1">
                          <MapPin className="h-3.5 w-3.5 shrink-0 text-slate-400" />
                          <span className="truncate">{row.locationName || 'Default location'}</span>
                        </div>
                        <div className="flex items-center gap-3 shrink-0">
                          <span className="text-slate-600 tabular-nums text-xs">{row.quantity} on hand</span>
                          <Button
                            size="sm"
                            onClick={() => openTake(part, row)}
                            disabled={row.quantity <= 0}
                            data-testid={`take-${part.id}-${row.id}`}
                            className="h-9 px-4 bg-slate-900 hover:bg-slate-800"
                          >
                            <Minus className="h-4 w-4 mr-1" /> Take
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </main>

      {/* Take dialog */}
      <Dialog open={!!takeRow} onOpenChange={(o) => !o && setTakeRow(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Take from stock</DialogTitle>
            <DialogDescription>
              {takeRow && (
                <>
                  <span className="font-semibold text-slate-900">{takeRow.part.name}</span>
                  {takeRow.stockRow.locationName ? ` · ${takeRow.stockRow.locationName}` : ''}
                  {' · '}
                  <span className="font-mono">{takeRow.stockRow.quantity} on hand</span>
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Quantity to take *</Label>
              <Input
                className="mt-1.5"
                type="number"
                min="1"
                max={takeRow?.stockRow.quantity ?? 1}
                value={qty}
                onChange={e => setQty(e.target.value)}
                data-testid="take-qty-input"
              />
              {takeRow && (
                <p className="text-xs text-slate-500 mt-1">Max {takeRow.stockRow.quantity}.</p>
              )}
            </div>
            <div>
              <Label>What's it for? (optional)</Label>
              <Textarea
                className="mt-1.5"
                rows={3}
                value={notes}
                onChange={e => setNotes(e.target.value)}
                placeholder="e.g. Replacing belt on Conveyor A"
                data-testid="take-notes-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTakeRow(null)} disabled={submitting}>Cancel</Button>
            <Button
              onClick={submitTake}
              disabled={submitting}
              className="bg-slate-900 hover:bg-slate-800"
              data-testid="confirm-take-btn"
            >
              {submitting ? 'Submitting...' : 'Confirm pickup'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <PoweredByLutrom />

      <QrScanner
        open={scanOpen}
        onClose={() => setScanOpen(false)}
        onScan={handleScan}
        title={quickMode ? 'Quick scan — take 1' : 'Scan part barcode'}
        hint={
          quickMode
            ? "Quick mode: each scan takes 1 and re-opens the camera. Tap Cancel when you're done."
            : "Point your camera at the barcode or QR code on the part. We'll find it in this store and open the take dialog."
        }
      />
    </div>
  );
};

export default PublicStorePickup;
